<template>
  <div>
    <h1 class="text-2xl font-bold text-gray-900 mb-6">Pedidos</h1>
    
    <!-- Filters -->
    <div class="flex gap-2 mb-4 overflow-x-auto pb-2">
      <button
        v-for="filter in filters"
        :key="filter.value"
        @click="selectedFilter = filter.value"
        class="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
        :class="{
          'bg-orange-100 text-orange-700': selectedFilter === filter.value,
          'bg-white text-gray-700 hover:bg-gray-50 border': selectedFilter !== filter.value
        }"
      >
        {{ filter.label }}
      </button>
    </div>
    
    <!-- Advanced filters -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Data de</label>
          <input
            v-model="filterDateFrom"
            type="date"
            class="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Data até</label>
          <input
            v-model="filterDateTo"
            type="date"
            class="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Entregador</label>
          <select
            v-model="filterDriverId"
            class="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option :value="null">Todos</option>
            <option v-for="driver in drivers" :key="driver.id" :value="driver.id">
              {{ driver.display_name || driver.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Cliente</label>
          <input
            v-model="filterCustomerSearch"
            type="text"
            placeholder="Nome ou telefone"
            class="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div class="flex gap-2">
          <button
            @click="applyFilters"
            class="flex-1 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
          >
            Filtrar
          </button>
          <button
            @click="clearFilters"
            class="px-4 py-2 bg-white text-gray-700 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Limpar
          </button>
        </div>
      </div>
    </div>
    
    <!-- Orders List -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full table-fixed divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32 cursor-pointer select-none hover:text-orange-600"
                @click="toggleSort('created_at')"
              >
                <span class="inline-flex items-center gap-1">
                  Data do pedido
                  <SortIcon column="created_at" />
                </span>
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-36 cursor-pointer select-none hover:text-orange-600"
                @click="toggleSort('order_number')"
              >
                <span class="inline-flex items-center gap-1">
                  Pedido
                  <SortIcon column="order_number" />
                </span>
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-48 cursor-pointer select-none hover:text-orange-600"
                @click="toggleSort('customer_name')"
              >
                <span class="inline-flex items-center gap-1">
                  Cliente
                  <SortIcon column="customer_name" />
                </span>
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20 cursor-pointer select-none hover:text-orange-600"
                @click="toggleSort('total')"
              >
                <span class="inline-flex items-center gap-1">
                  Total
                  <SortIcon column="total" />
                </span>
              </th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Pagamento</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Status</th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32 cursor-pointer select-none hover:text-orange-600"
                @click="toggleSort('driver_name')"
              >
                <span class="inline-flex items-center gap-1">
                  Motorista
                  <SortIcon column="driver_name" />
                </span>
              </th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Saída</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-36">Ações</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="order in orders" :key="order.id" class="hover:bg-gray-50">
              <td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500" :title="order.created_at ? formatDateTime(order.created_at) : '-'">
                {{ order.created_at ? formatDateTime(order.created_at) : '-' }}
              </td>
              <td class="px-3 py-3 text-sm font-medium truncate">
                <button
                  @click="openDetailModal(order)"
                  @mouseenter="showItemsPreview(order, $event)"
                  @mouseleave="hideItemsPreview"
                  class="text-orange-600 hover:text-orange-800 hover:underline"
                >
                  #{{ order.order_number }}
                </button>
              </td>
              <td class="px-3 py-3 text-sm text-gray-500">
                <div class="flex items-center gap-2">
                  <span class="truncate max-w-[120px]" :title="order.customer_name || order.customer?.name || 'N/A'">
                    {{ order.customer_name || order.customer?.name || 'N/A' }}
                  </span>
                  <button
                    v-if="order.delivery_address"
                    @click="openAddressModal(order)"
                    class="text-orange-600 hover:text-orange-800 shrink-0"
                    title="Ver endereço de entrega"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </button>
                </div>
              </td>
              <td class="px-3 py-3 whitespace-nowrap text-sm font-medium">{{ formatCurrency(order.total || 0) }}</td>
              
              <!-- Coluna Pagamento -->
              <td class="px-3 py-3 whitespace-nowrap text-sm">
                <div class="flex flex-col items-start gap-1">
                  <button
                    v-if="order.payment_status && order.payment_status !== 'pending'"
                    @click="openPaymentModal(order)"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors hover:opacity-80"
                    :class="getPaymentStatusClass(order.payment_status)"
                  >
                    {{ getPaymentStatusLabel(order.payment_status) }}
                  </button>
                  <button
                    v-else
                    @click="openPaymentModal(order)"
                    class="text-orange-600 hover:text-orange-800 text-xs font-medium underline"
                  >
                    Aguardando
                  </button>
                  <button
                    v-if="order.payment_status !== 'paid'"
                    @click="markOrderAsPaid(order)"
                    title="Marcar como pago"
                    class="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium"
                  >
                    ✓ Marcar pago
                  </button>
                </div>
              </td>
              
              <td class="px-3 py-3 whitespace-nowrap">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="getStatusClass(order.status)">
                  {{ getStatusLabel(order.status) }}
                </span>
              </td>
              
              <!-- Coluna Motorista -->
              <td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500 max-w-[120px] truncate" :title="order.driver_name || '-'">
                {{ order.driver_name || '-' }}
              </td>
              
              <!-- Coluna Saída -->
              <td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500 max-w-[110px] truncate" :title="order.out_for_delivery_at ? formatDateTime(order.out_for_delivery_at) : '-'">
                {{ order.out_for_delivery_at ? formatDateTime(order.out_for_delivery_at) : '-' }}
              </td>
              
              <td class="px-3 py-3 whitespace-nowrap text-sm">
                <select
                  :key="selectKey + '-' + order.id"
                  :value="order.status"
                  @change="handleStatusChange(order.id, ($event.target as HTMLSelectElement).value)"
                  class="text-xs border rounded-lg px-1 py-1 w-full"
                >
                  <option value="confirmed">1- Confirmar</option>
                  <option value="preparing">2- Iniciar Preparo</option>
                  <option value="ready">3- Pronto</option>
                  <option value="out_for_delivery">4- Saiu para entrega</option>
                  <option value="delivered">5- Entregue</option>
                  <option value="cancelled">6- Cancelar</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div v-if="orders.length === 0" class="p-8 text-center text-gray-500">
        Nenhum pedido encontrado
      </div>
      
      <!-- Pagination -->
      <div v-if="totalItems > 0" class="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 border-t border-gray-200">
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-600">Registros por página:</label>
          <select
            v-model="itemsPerPage"
            @change="onItemsPerPageChange"
            class="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
          </select>
          <span class="text-sm text-gray-600">
            Mostrando {{ startItem }} a {{ endItem }} de {{ totalItems }}
          </span>
        </div>
        
        <div class="flex items-center gap-1">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            class="px-3 py-1 text-sm border rounded-lg"
            :class="page === currentPage ? 'bg-orange-600 text-white border-orange-600' : 'hover:bg-gray-50'"
          >
            {{ page }}
          </button>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>
    
    <!-- Modal de Seleção de Motorista -->
    <div v-if="showDriverModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closeDriverModal">
      <div class="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-900">Selecionar Motorista</h3>
          <button @click="closeDriverModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div v-if="drivers.length === 0" class="text-center py-8 text-gray-500">
          <p class="mb-2">Nenhum entregador cadastrado.</p>
          <router-link to="/admin/drivers" class="text-orange-600 hover:text-orange-800 font-medium underline">
            Cadastrar entregadores
          </router-link>
        </div>
        
        <div v-else class="space-y-2 max-h-80 overflow-y-auto">
          <button
            v-for="driver in drivers"
            :key="driver.id"
            @click="selectDriver(driver.id)"
            class="w-full text-left px-4 py-3 rounded-lg border hover:bg-orange-50 hover:border-orange-200 transition-colors"
            :class="selectedDriverId === driver.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200'"
          >
            <div class="font-medium text-gray-900">
              {{ driver.display_name || driver.name }}
            </div>
            <div class="text-xs text-gray-500 mt-0.5">
              {{ driver.driver_type === 'company' ? 'Pessoa Jurídica' : 'Pessoa Física' }}
              <span v-if="driver.cpf"> • CPF: {{ driver.cpf }}</span>
              <span v-if="driver.cnpj"> • CNPJ: {{ driver.cnpj }}</span>
            </div>
          </button>
        </div>
        
        <div class="flex justify-end gap-3 mt-6">
          <button @click="closeDriverModal" class="px-4 py-2 text-gray-600 hover:text-gray-800">
            Cancelar
          </button>
          <button
            @click="confirmDriverSelection"
            :disabled="!selectedDriverId || saving"
            class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            {{ saving ? 'Salvando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Modal de Endereço de Entrega -->
    <div v-if="showAddressModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closeAddressModal">
      <div class="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-900">Endereço de Entrega</h3>
          <button @click="closeAddressModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div v-if="selectedOrder" class="space-y-3">
          <div class="bg-gray-50 p-4 rounded-lg space-y-2">
            <p class="text-sm text-gray-600">Cliente: <span class="font-medium text-gray-900">{{ selectedOrder.customer_name || selectedOrder.customer?.name || 'N/A' }}</span></p>
            <p class="text-sm text-gray-600">Pedido: <span class="font-medium text-gray-900">#{{ selectedOrder.order_number }}</span></p>
          </div>
          
          <div class="space-y-2">
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase mb-0.5">Endereço</label>
              <p class="text-sm font-medium text-gray-900">{{ selectedOrder.delivery_address }}</p>
            </div>
            <div v-if="selectedOrder.delivery_neighborhood">
              <label class="block text-xs font-medium text-gray-500 uppercase mb-0.5">Bairro</label>
              <p class="text-sm font-medium text-gray-900">{{ selectedOrder.delivery_neighborhood }}</p>
            </div>
            <div v-if="selectedOrder.delivery_city">
              <label class="block text-xs font-medium text-gray-500 uppercase mb-0.5">Cidade</label>
              <p class="text-sm font-medium text-gray-900">{{ selectedOrder.delivery_city }}</p>
            </div>
            <div v-if="selectedOrder.delivery_complement">
              <label class="block text-xs font-medium text-gray-500 uppercase mb-0.5">Complemento</label>
              <p class="text-sm font-medium text-gray-900">{{ selectedOrder.delivery_complement }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Detalhes do Pagamento -->
    <div v-if="showPaymentModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closePaymentModal">
      <div class="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-900">Detalhes do Pagamento</h3>
          <button @click="closePaymentModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div v-if="selectedOrder" class="space-y-4">
          <!-- Info do Pedido -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <p class="text-sm text-gray-600">Pedido: <span class="font-medium text-gray-900">#{{ selectedOrder.order_number }}</span></p>
            <p class="text-sm text-gray-600">Cliente: <span class="font-medium text-gray-900">{{ selectedOrder.customer_name || selectedOrder.customer?.name || 'N/A' }}</span></p>
            <p class="text-sm text-gray-600">Total: <span class="font-medium text-gray-900">{{ formatCurrency(selectedOrder.total || 0) }}</span></p>
          </div>
          
          <!-- Forma de Pagamento -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Forma de Pagamento</label>
            <p class="text-lg font-medium text-gray-900">
              {{ getPaymentMethodLabel(selectedOrder.payment_method) }}
            </p>
          </div>
          
          <!-- Status do Pagamento -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Status do Pagamento</label>
            <div class="flex items-center gap-3">
              <span 
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                :class="getPaymentStatusClass(selectedOrder.payment_status)"
              >
                {{ getPaymentStatusLabel(selectedOrder.payment_status) }}
              </span>
              <button
                v-if="selectedOrder.payment_external_id"
                @click="refreshPaymentStatus(selectedOrder.id)"
                :disabled="refreshing"
                class="text-sm text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
              >
                <svg v-if="refreshing" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                {{ refreshing ? 'Atualizando...' : 'Atualizar' }}
              </button>
            </div>
          </div>
          
          <!-- ID Externo (Mercado Pago) -->
          <div v-if="selectedOrder.payment_external_id">
            <label class="block text-xs font-medium text-gray-500 uppercase mb-1">ID Mercado Pago</label>
            <p class="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">{{ selectedOrder.payment_external_id }}</p>
          </div>
          
          <!-- PIX Code (se for PIX) -->
          <div v-if="selectedOrder.payment_method === 'pix' && selectedOrder.payment_pix_code">
            <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Código PIX</label>
            <div class="flex gap-2">
              <code class="flex-1 text-xs break-all bg-gray-100 px-2 py-1 rounded">{{ selectedOrder.payment_pix_code }}</code>
              <button 
                @click="copyPixCode(selectedOrder.payment_pix_code)"
                class="text-orange-600 hover:text-orange-800 text-sm font-medium"
              >
                Copiar
              </button>
            </div>
          </div>
          
          <!-- Mensagem quando não há pagamento externo -->
          <div v-if="!selectedOrder.payment_external_id && selectedOrder.payment_method === 'cash'" class="bg-blue-50 p-4 rounded-lg">
            <p class="text-sm text-blue-700">
              <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Pagamento em dinheiro. Aguardar pagamento na entrega.
            </p>
          </div>
          
          <div v-else-if="!selectedOrder.payment_external_id" class="bg-yellow-50 p-4 rounded-lg">
            <p class="text-sm text-yellow-700">
              <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              Pagamento ainda não processado no Mercado Pago.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhe do Pedido -->
    <div v-if="showDetailModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closeDetailModal">
      <div class="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl max-h-[85vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-900">Pedido #{{ selectedOrder?.order_number }}</h3>
          <button @click="closeDetailModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div v-if="loadingDetail" class="py-10 text-center text-gray-500">Carregando...</div>

        <div v-else-if="selectedOrder" class="space-y-4">
          <!-- Info geral -->
          <div class="bg-gray-50 p-4 rounded-lg space-y-1">
            <p class="text-sm text-gray-600">Cliente: <span class="font-medium text-gray-900">{{ selectedOrder.customer_name || selectedOrder.customer?.name || 'N/A' }}</span></p>
            <p class="text-sm text-gray-600">Data: <span class="font-medium text-gray-900">{{ formatDateTime(selectedOrder.created_at) }}</span></p>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-600">Status:</span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="getStatusClass(selectedOrder.status)">
                {{ getStatusLabel(selectedOrder.status) }}
              </span>
            </div>
          </div>

          <!-- Itens -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase mb-2">Itens</label>
            <ul class="divide-y divide-gray-100 border rounded-lg">
              <li v-for="item in selectedOrder.items" :key="item.id" class="p-3">
                <div class="flex justify-between text-sm font-medium text-gray-900">
                  <span>{{ item.quantity }}x {{ item.product_name }}</span>
                  <span>{{ formatCurrency(item.total) }}</span>
                </div>
                <p v-if="item.selected_options?.length" class="text-xs text-gray-500 mt-0.5">
                  {{ item.selected_options.map(o => o.value).join(', ') }}
                </p>
                <p v-if="item.selected_addons?.length" class="text-xs text-gray-500 mt-0.5">
                  + {{ item.selected_addons.map(a => a.name).join(', ') }}
                </p>
                <p v-if="item.notes" class="text-xs text-gray-500 italic mt-0.5">Obs: {{ item.notes }}</p>
              </li>
            </ul>
          </div>

          <!-- Endereço -->
          <div v-if="selectedOrder.delivery_address">
            <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Endereço de entrega</label>
            <p class="text-sm text-gray-900">
              {{ selectedOrder.delivery_address }}
              <span v-if="selectedOrder.delivery_neighborhood">, {{ selectedOrder.delivery_neighborhood }}</span>
              <span v-if="selectedOrder.delivery_complement"> - {{ selectedOrder.delivery_complement }}</span>
            </p>
          </div>

          <!-- Pagamento e totais -->
          <div class="border-t pt-3 space-y-1">
            <div class="flex justify-between text-sm text-gray-600">
              <span>Forma de pagamento</span>
              <span class="font-medium text-gray-900">{{ getPaymentMethodLabel(selectedOrder.payment_method) }}</span>
            </div>
            <div class="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{{ formatCurrency(selectedOrder.subtotal || 0) }}</span>
            </div>
            <div class="flex justify-between text-sm text-gray-600">
              <span>Taxa de entrega</span>
              <span>{{ formatCurrency(selectedOrder.delivery_fee || 0) }}</span>
            </div>
            <div class="flex justify-between text-sm font-bold text-gray-900">
              <span>Total</span>
              <span>{{ formatCurrency(selectedOrder.total || 0) }}</span>
            </div>
          </div>

          <p v-if="selectedOrder.customer_notes" class="text-sm text-gray-600 italic border-t pt-3">
            Obs. do cliente: {{ selectedOrder.customer_notes }}
          </p>
        </div>
      </div>
    </div>

    <!-- Tooltip com os itens do pedido (fora da tabela via Teleport, para não ser cortada pelo overflow-x-auto) -->
    <Teleport to="body">
      <div
        v-if="hoveredOrder"
        class="fixed z-50 w-64 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 pointer-events-none"
        :style="{ top: tooltipPos.top + 'px', left: tooltipPos.left + 'px' }"
      >
        <p v-if="itemsPreviewCache[hoveredOrder.id] === undefined" class="text-gray-300">Carregando itens...</p>
        <ul v-else-if="itemsPreviewCache[hoveredOrder.id].length" class="space-y-1">
          <li v-for="item in itemsPreviewCache[hoveredOrder.id]" :key="item.id" class="flex justify-between gap-2">
            <span class="truncate">{{ item.quantity }}x {{ item.product_name }}</span>
            <span class="shrink-0 text-gray-300">{{ formatCurrency(item.total) }}</span>
          </li>
        </ul>
        <p v-else class="text-gray-300">Sem itens</p>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, h } from 'vue'
import { useAuthStore } from '@/stores'
import { orderApi, paymentApi, driverApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError, notifySuccess } from '@/utils/notifications'
import { confirmDialog } from '@/composables/useConfirm'
import type { Order, DeliveryDriver } from '@/types'

const SortIcon = (props: { column: string }) => {
  if (sortBy.value !== props.column) {
    return h('svg', { class: 'w-3 h-3 text-gray-300', fill: 'currentColor', viewBox: '0 0 20 20' }, [
      h('path', { d: 'M5 10l5-5 5 5H5zM15 10l-5 5-5-5h10z' })
    ])
  }
  const isAsc = sortOrder.value === 'asc'
  return h('svg', { class: 'w-3 h-3 text-orange-600', fill: 'currentColor', viewBox: '0 0 20 20' }, [
    h('path', { d: isAsc ? 'M5 15l5-5 5 5H5z' : 'M5 5l5 5 5-5H5z' })
  ])
}

const authStore = useAuthStore()

const orders = ref<Order[]>([])
const drivers = ref<DeliveryDriver[]>([])
const selectedFilter = ref('all')
const showPaymentModal = ref(false)
const showDriverModal = ref(false)
const showAddressModal = ref(false)
const showDetailModal = ref(false)
const loadingDetail = ref(false)
const selectedOrder = ref<Order | null>(null)
// Cache dos itens de cada pedido, usado no tooltip ao passar o mouse (chave: order.id)
const itemsPreviewCache = ref<Record<number, Order['items']>>({})
const hoveredOrder = ref<Order | null>(null)
const tooltipPos = ref({ top: 0, left: 0 })
const selectedDriverId = ref<number | null>(null)
const pendingOrderId = ref<number | null>(null)
const pendingStatus = ref('')
const refreshing = ref(false)
const saving = ref(false)
const selectKey = ref(0)
let refreshInterval: ReturnType<typeof setInterval> | null = null

// Pagination
const pageSizes = [10, 20, 50, 100]
const itemsPerPage = ref(20)
const currentPage = ref(1)
const totalItems = ref(0)

// Sorting
const sortBy = ref('created_at')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Advanced filters
const filterDateFrom = ref('')
const filterDateTo = ref('')
const filterDriverId = ref<number | null>(null)
const filterCustomerSearch = ref('')

const filters = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'confirmed', label: 'Confirmados' },
  { value: 'preparing', label: 'Em preparo' },
  { value: 'ready', label: 'Prontos' },
  { value: 'out_for_delivery', label: 'Em entrega' },
  { value: 'delivered', label: 'Entregues' }
]

const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / itemsPerPage.value)))
const startItem = computed(() => totalItems.value === 0 ? 0 : (currentPage.value - 1) * itemsPerPage.value + 1)
const endItem = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalItems.value))

const visiblePages = computed(() => {
  const pages: number[] = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

const startAutoRefresh = () => {
  stopAutoRefresh()
  refreshInterval = setInterval(() => {
    loadOrders()
  }, 180000) // 3 minutos
}

const stopAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

const getStatusClass = (status: string) => {
  const classes: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    ready: 'bg-indigo-100 text-indigo-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    preparing: 'Em Preparo',
    ready: 'Pronto',
    out_for_delivery: 'Em Entrega',
    delivered: 'Entregue',
    cancelled: 'Cancelado'
  }
  return labels[status] || status
}

const getPaymentStatusClass = (status: string | undefined) => {
  const classes: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-600',
    processing: 'bg-blue-100 text-blue-800',
    paid: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-orange-100 text-orange-800'
  }
  return classes[status || 'pending'] || 'bg-gray-100 text-gray-600'
}

const getPaymentStatusLabel = (status: string | undefined) => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    processing: 'Processando',
    paid: 'Pago',
    failed: 'Falhou',
    refunded: 'Reembolsado'
  }
  return labels[status || 'pending'] || 'Pendente'
}

const getPaymentMethodLabel = (method: string | undefined) => {
  const labels: Record<string, string> = {
    credit_card: 'Cartão de Crédito',
    debit_card: 'Cartão de Débito',
    pix: 'PIX',
    cash: 'Dinheiro',
    mercado_pago: 'Mercado Pago'
  }
  return labels[method || ''] || method || 'Não informado'
}

const formatDateTime = (value: string | undefined) => {
  if (!value) return '-'
  const date = new Date(value)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const toggleSort = (column: string) => {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'desc'
  }
  currentPage.value = 1
  loadOrders()
}

const applyFilters = () => {
  currentPage.value = 1
  loadOrders()
}

const clearFilters = () => {
  filterDateFrom.value = ''
  filterDateTo.value = ''
  filterDriverId.value = null
  filterCustomerSearch.value = ''
  selectedFilter.value = 'all'
  sortBy.value = 'created_at'
  sortOrder.value = 'desc'
  currentPage.value = 1
  loadOrders()
}

const onItemsPerPageChange = () => {
  currentPage.value = 1
  loadOrders()
}

const goToPage = (page: number) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  loadOrders()
}

const openPaymentModal = (order: Order) => {
  selectedOrder.value = order
  showPaymentModal.value = true
}

const closePaymentModal = () => {
  showPaymentModal.value = false
  selectedOrder.value = null
}

const openAddressModal = (order: Order) => {
  selectedOrder.value = order
  showAddressModal.value = true
}

// Busca os itens do pedido para o tooltip (uma vez só, com cache por order.id)
const fetchOrderItemsPreview = async (order: Order) => {
  if (itemsPreviewCache.value[order.id] !== undefined) return
  try {
    const response = await orderApi.get(order.id)
    itemsPreviewCache.value[order.id] = response.data.items || []
  } catch {
    // Falha silenciosa: tooltip simplesmente não mostra itens
    delete itemsPreviewCache.value[order.id]
  }
}

const showItemsPreview = (order: Order, event: MouseEvent) => {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  tooltipPos.value = { top: rect.bottom + 4, left: rect.left }
  hoveredOrder.value = order
  fetchOrderItemsPreview(order)
}

const hideItemsPreview = () => {
  hoveredOrder.value = null
}

const openDetailModal = async (order: Order) => {
  showDetailModal.value = true
  loadingDetail.value = true
  try {
    const response = await orderApi.get(order.id)
    selectedOrder.value = response.data
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao carregar detalhes do pedido')
    showDetailModal.value = false
  } finally {
    loadingDetail.value = false
  }
}

const closeDetailModal = () => {
  showDetailModal.value = false
  selectedOrder.value = null
}

const closeAddressModal = () => {
  showAddressModal.value = false
  selectedOrder.value = null
}

const closeDriverModal = () => {
  showDriverModal.value = false
  selectedDriverId.value = null
  pendingOrderId.value = null
  pendingStatus.value = ''
  selectKey.value++
}

const selectDriver = (driverId: number) => {
  selectedDriverId.value = driverId
}

const confirmDriverSelection = async () => {
  if (!pendingOrderId.value || !selectedDriverId.value) return
  
  saving.value = true
  try {
    await orderApi.updateStatus(pendingOrderId.value, {
      status: pendingStatus.value,
      driver_id: selectedDriverId.value
    })
    notifySuccess('Status atualizado!')
    closeDriverModal()
    loadOrders()
  } catch (error) {
    notifyError('Erro ao atualizar status')
    selectKey.value++
  } finally {
    saving.value = false
  }
}

const refreshPaymentStatus = async (orderId: number) => {
  refreshing.value = true
  try {
    const response = await paymentApi.refreshStatus(orderId)
    if (response.data.success) {
      const index = orders.value.findIndex(o => o.id === orderId)
      if (index !== -1) {
        orders.value[index].payment_status = response.data.new_status
      }
      if (selectedOrder.value) {
        selectedOrder.value.payment_status = response.data.new_status
      }
      notifySuccess('Status atualizado com sucesso!')
      loadOrders()
    } else {
      notifyError(response.data.message || 'Não foi possível atualizar o status')
    }
  } catch (error: any) {
    notifyError('Erro ao atualizar status: ' + (error.response?.data?.detail || 'Erro desconhecido'))
  } finally {
    refreshing.value = false
  }
}

const copyPixCode = (code: string) => {
  navigator.clipboard.writeText(code)
  notifySuccess('Código PIX copiado!')
}

const loadOrders = async () => {
  const companyId = authStore.companyId || 1
  try {
    const params: Record<string, any> = {
      skip: (currentPage.value - 1) * itemsPerPage.value,
      limit: itemsPerPage.value,
      sort_by: sortBy.value,
      sort_order: sortOrder.value
    }
    
    if (selectedFilter.value !== 'all') {
      params.status = selectedFilter.value
    }
    
    if (filterDateFrom.value) {
      params.date_from = filterDateFrom.value
    }
    if (filterDateTo.value) {
      params.date_to = filterDateTo.value
    }
    if (filterDriverId.value) {
      params.driver_id = filterDriverId.value
    }
    if (filterCustomerSearch.value.trim()) {
      params.customer_search = filterCustomerSearch.value.trim()
    }
    
    const res = await orderApi.getByCompany(companyId, params)
    orders.value = res.data.items || res.data
    totalItems.value = res.data.total !== undefined ? res.data.total : orders.value.length
  } catch (error) {
    console.error('Erro ao carregar pedidos:', error)
  }
}

const loadDrivers = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await driverApi.getByCompany(companyId, true)
    drivers.value = res.data
  } catch (error) {
    console.error('Erro ao carregar entregadores:', error)
  }
}

const handleStatusChange = async (orderId: number, status: string) => {
  if (!status) return
  
  // Se for saiu para entrega, abre modal de seleção de motorista
  if (status === 'out_for_delivery') {
    pendingOrderId.value = orderId
    pendingStatus.value = status
    selectedDriverId.value = null
    await loadDrivers()
    showDriverModal.value = true
    return
  }
  
  try {
    await orderApi.updateStatus(orderId, { status })
    notifySuccess('Status atualizado!')
    loadOrders()
  } catch (error) {
    notifyError('Erro ao atualizar status')
  }
}

const markOrderAsPaid = async (order: Order) => {
  if (!await confirmDialog({ title: 'Confirmar pagamento', message: `Marcar o pedido #${order.order_number} como pago?`, danger: false, confirmLabel: 'Marcar como pago' })) return
  
  try {
    await paymentApi.markAsPaid(order.id)
    notifySuccess('Pagamento marcado como pago!')
    loadOrders()
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao marcar como pago')
  }
}

onMounted(() => {
  loadDrivers()
  loadOrders()
  startAutoRefresh()
})

onUnmounted(() => {
  stopAutoRefresh()
})
</script>
